import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { usePreference, PREF_KEYS } from '@/hooks/useUserPreferences';
import { groupDemand, fitCheck, sortByHeadroom, sortByBestFit, sortBySoonest, } from '@/lib/matchedDemand';
import { categoryLabel } from '@/lib/buyRequestFilter';
import { PRODUCT_FORM_CATEGORIES } from '@/lib/productFormData';
import { StickyToolbar } from '@/components/layout/StickyToolbar';
import { FilterPill } from '@/components/listings/FilterPill';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, Inbox, Search, ChevronDown, EyeOff } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { NumText } from '@/components/ui/num-text';
import NewMessageDialog from '@/components/messages/NewMessageDialog';
import { GRAMS_PER_LB } from '@/lib/buyRequestParser';
import { formatNumber } from '@/lib/utils';
import { formatLbs, formatThc } from '@/lib/figures';
import FitStrip from '@/components/buyer/FitStrip';
import ListingMatchRow from '@/components/buyer/ListingMatchRow';
import { buyRequestDraft, curatedOfferDraft } from '@/lib/deskReply';
import CuratedOfferDialog, { type CuratedOfferItem } from '@/components/buyer/CuratedOfferDialog';
import { useFeatureRegistration } from '@/contexts/FeatureRegistryContext';
import { closestGradeBadge, closestGradeNotice } from '@/lib/closestGrade';

export const FEATURE_META = {
  id: 'matched-demand',
  label: 'Matched Demand',
  description: 'Buy Requests from buyers on your menus that one of your listings fits, with a ready-to-edit message.',
  triggerType: 'panel' as const,
  personas: ['seller', 'buyer_seller'] as Array<'seller' | 'buyer_seller'>,
};

export interface DemandItem {
  match_id: string; request_id: string; buyer_company_id: string; buyer_company_name: string;
  raw_text: string; min_quantity_g: number | null; min_thc_percent: number | null;
  buyer_price_per_g: number | null; needed_by: string | null;
  listing_id: string; strain_name: string | null; product_form: string | null;
  price_per_gram: number | null; thc_percent: number | null; quantity_grams: number | null;
  image?: string | null;
  matched_at: string; contacted: boolean;
  /** Set when this listing is the closest grade to the one the buyer asked for. */
  closest_to?: string | null;
}
export interface MatchedDemand { persona: string; can_receive: boolean; items: DemandItem[] }

export function useMatchedDemand() {
  return useQuery({
    queryKey: ['matched-demand'],
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)('get_seller_matched_demand', { p_limit: 100 });
      if (error) throw error;
      return data as MatchedDemand;
    },
    staleTime: 60_000,
  });
}

const perLb = (g: number | null) => (g != null ? `$${formatNumber(Math.round(g * GRAMS_PER_LB), 0)}/lb` : '—');

type Show = 'open' | 'messaged' | 'all';
type Order = 'best' | 'headroom' | 'soonest' | 'newest';

const lbMoney = (perG: number) => `$${formatNumber(Math.round(Math.abs(perG) * GRAMS_PER_LB), 0)}/lb`;

/** Known specs of the offered listing, for the editable offer draft. Never names a source. */
function offerSpecs(m: DemandItem): string {
  return [
    m.price_per_gram != null ? perLb(m.price_per_gram) : null,
    formatThc(m.thc_percent) ? `${formatThc(m.thc_percent)} THC` : null,
    formatLbs(m.quantity_grams) ? `${formatLbs(m.quantity_grams)} available` : null,
  ].filter(Boolean).join(', ');
}

export default function MatchedDemandList({ items }: { items: DemandItem[] }) {
  useFeatureRegistration(FEATURE_META);
  const qc = useQueryClient();
  const [messaging, setMessaging] = useState<DemandItem | null>(null);
  const [offering, setOffering] = useState<DemandItem[] | null>(null);
  const [offerDraft, setOfferDraft] = useState<string | null>(null);
  const toOfferItem = (m: DemandItem): CuratedOfferItem => ({ id: m.match_id, title: m.strain_name ?? 'Listing', specs: offerSpecs(m), closestTo: m.closest_to, form: m.product_form });
  const [query, setQuery] = useState('');
  const [show, setShow] = useState<Show>('open');
  const [order, setOrder] = useState<Order>('best')
  const [category, setCategory] = useState<string | null>(null);;
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [dismissed, setDismissed] = usePreference<string[]>(PREF_KEYS.dismissedDemand, []);

  const groups = useMemo(() => groupDemand(items), [items]);
  const groupCats = (g: (typeof groups)[number]) => new Set(g.matches.map((m) => categoryLabel(m.product_form)).filter(Boolean));
  const categories = useMemo(() => {
    const present = new Set(groups.filter((g) => !dismissed.includes(g.request_id)).flatMap((g) => [...groupCats(g)]));
    return PRODUCT_FORM_CATEGORIES.filter((c) => present.has(c));
  }, [groups, dismissed]);
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = groups.filter((g) => {
      if (dismissed.includes(g.request_id)) return false;
      if (show === 'open' && g.contacted) return false;
      if (show === 'messaged' && !g.contacted) return false;
      if (category && !groupCats(g).has(category)) return false;
      if (!q) return true;
      const r = g.matches[0];
      return [r.buyer_company_name, r.raw_text, ...g.matches.map((m) => `${m.strain_name ?? ''} ${m.product_form ?? ''}`)]
        .join(' ').toLowerCase().includes(q);
    });
    if (order === 'headroom') return sortByHeadroom(filtered);
    if (order === 'best') return sortByBestFit(filtered);
    if (order === 'soonest') return sortBySoonest(filtered);
    return filtered;
  }, [groups, dismissed, show, query, order, category]);
  const hiddenCount = groups.filter((g) => dismissed.includes(g.request_id)).length;

  const dismiss = (id: string) => {
    setDismissed([...dismissed, id]);
    toast.success('Hidden from Matched demand', { action: { label: 'Undo', onClick: () => setDismissed(dismissed.filter((x) => x !== id)) } });
  };

  if (items.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-10 flex flex-col items-center text-center gap-2">
          <Inbox className="w-8 h-8 text-muted-foreground" />
          <p className="font-medium">No matched demand right now</p>
          <p className="text-sm text-muted-foreground max-w-md">When a buyer on your menus asks for something one of your listings fits, it shows up here — ready to answer.</p>
          <Button variant="outline" size="sm" className="mt-2" asChild><Link to="/my-listings">View my listings</Link></Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <StickyToolbar className="mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <ToggleGroup type="single" value={show} onValueChange={(v) => v && setShow(v as Show)} size="sm" aria-label="Show">
            <ToggleGroupItem value="open">To answer</ToggleGroupItem>
            <ToggleGroupItem value="messaged">Messaged</ToggleGroupItem>
            <ToggleGroupItem value="all">All</ToggleGroupItem>
          </ToggleGroup>
          <div className="relative min-w-[200px] flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search buyers, requests or your listings" className="pl-9" aria-label="Search matched demand" />
          </div>
          <Select value={order} onValueChange={(v) => setOrder(v as Order)}>
            <SelectTrigger className="h-9 w-full sm:w-48" aria-label="Sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="best">Best fit</SelectItem>
              <SelectItem value="headroom">Most room to earn</SelectItem>
              <SelectItem value="soonest">Needed soonest</SelectItem>
              <SelectItem value="newest">Newest first</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {categories.length > 1 && (
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <FilterPill key={c} label={c} active={category === c} onToggle={() => setCategory(category === c ? null : c)} />
            ))}
          </div>
        )}
      </StickyToolbar>

      {visible.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">
          {show === 'open' && !query && !category ? "You've answered every request. Nice work." : 'Nothing matches that.'}
        </p>
      ) : (
        <div className="space-y-3">
          {visible.map((g) => {
            const best = g.matches[0];
            const others = g.matches.slice(1);
            const open = !!expanded[g.request_id];
            const fit = fitCheck(best);
            const askThc = formatThc(best.min_thc_percent);
            const askQty = formatLbs(best.min_quantity_g, '+ lbs');
            const haveThc = formatThc(best.thc_percent);
            const haveQty = formatLbs(best.quantity_grams);
            return (
              <Card key={g.request_id}>
                <CardContent className="p-4 sm:p-5">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold truncate">{best.buyer_company_name}</p>
                        {g.contacted ? <Badge variant="outline">Messaged</Badge> : <Badge variant="secondary">New</Badge>}
                      </div>
                      <p className="text-sm mt-1" title={best.raw_text}>“{best.raw_text}”</p>
                      {best.needed_by && <p className="mt-1 text-xs text-muted-foreground">Needed by {new Date(best.needed_by).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</p>}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {fit.headroomPerG != null && fit.headroomPerG > 0 && (
                        <span className="rounded-full border border-foreground/20 bg-muted px-2.5 py-0.5 text-xs font-semibold text-foreground" title="Their ceiling minus your price">
                          <NumText role="num-cell">{`+${lbMoney(fit.headroomPerG)} room`}</NumText>
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">{new Date(g.latest).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                    </div>
                  </div>

                  <div className="mt-3">
                    <FitStrip askLabel="They asked" haveLabel="You have" cells={[
                      { label: 'Price', ask: best.buyer_price_per_g != null ? `Up to ${perLb(best.buyer_price_per_g)}` : 'Open', have: perLb(best.price_per_gram), status: fit.price },
                      { label: 'THC', ask: askThc ? `${askThc}+` : 'Open', have: haveThc ?? '—', status: fit.thc },
                      { label: 'Quantity', ask: askQty ?? 'Open', have: haveQty ?? '—', status: fit.quantity },
                    ]} />
                  </div>

                  <p className="mt-3 mb-1 text-xs text-muted-foreground">{best.closest_to ? closestGradeNotice(best.closest_to) : 'Your best fit'}</p>
                  <ul className="divide-y rounded-md border">
                    <ListingMatchRow
                      image={best.image}
                      title={best.strain_name ?? 'Listing'}
                      form={best.product_form}
                      badge={best.closest_to ? <Badge className="ml-2" variant="outline">{closestGradeBadge()}</Badge> : null}
                      href={`/listings/${best.listing_id}`}
                      pricePerG={best.price_per_gram}
                      thcPercent={best.thc_percent}
                      quantityG={best.quantity_grams}
                      primary={
                        <Button size="sm" variant="outline" onClick={() => (others.length > 0 ? setOffering(g.matches) : setMessaging(best))}>
                          <MessageSquare className="w-4 h-4" />{others.length > 0 ? 'Prepare offer' : g.contacted ? 'Message again' : 'Message offer'}
                        </Button>
                      }
                    />
                    {others.length > 0 && (
                      <li className={`grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr] !border-t-0'}`}>
                        <ul className="overflow-hidden divide-y" aria-hidden={!open} {...(!open ? { inert: '' as any } : {})}>
                    {others.map((d) => (
                      <ListingMatchRow
                        key={d.match_id}
                        image={d.image}
                        title={d.strain_name ?? 'Listing'}
                        form={d.product_form}
                        badge={d.closest_to ? <Badge className="ml-2" variant="outline">{closestGradeBadge()}</Badge> : null}
                        href={`/listings/${d.listing_id}`}
                        pricePerG={d.price_per_gram}
                        thcPercent={d.thc_percent}
                        quantityG={d.quantity_grams}
                        primary={<Button size="sm" variant="quiet" onClick={() => setMessaging(d)}>Offer this</Button>}
                      />
                    ))}
                        </ul>
                      </li>
                    )}
                  </ul>

                  <div className="mt-2 flex items-center justify-between gap-2">
                    {others.length > 0 ? (
                      <Button size="sm" variant="quiet" onClick={() => setExpanded({ ...expanded, [g.request_id]: !open })}>
                        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
                        {open ? 'Show less' : `${others.length} more ${others.length === 1 ? 'listing fits' : 'listings fit'}`}
                      </Button>
                    ) : <span />}
                    <Button size="sm" variant="quiet" onClick={() => dismiss(g.request_id)}>
                      <EyeOff className="w-4 h-4" />Not a fit
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      {hiddenCount > 0 && (
        <div className="mt-4 text-center">
          <Button size="sm" variant="quiet" onClick={() => setDismissed([])}>Show {hiddenCount} hidden</Button>
        </div>
      )}
      <NewMessageDialog
        open={!!messaging}
        onOpenChange={(o) => { if (!o) { setMessaging(null); setOfferDraft(null); } }}
        initialCompanyId={messaging?.buyer_company_id ?? null}
        initialDraft={offerDraft ?? (messaging ? buyRequestDraft({ listing: messaging.strain_name, specs: offerSpecs(messaging), want: messaging.raw_text.slice(0, 120), closestTo: messaging.closest_to ?? undefined, form: messaging.product_form ?? undefined }) : null)}
        onSent={() => qc.invalidateQueries({ queryKey: ['matched-demand'] })}
      />
      <CuratedOfferDialog
        open={!!offering}
        onOpenChange={(o) => !o && setOffering(null)}
        buyer={offering?.[0]?.buyer_company_name ?? 'the buyer'}
        want={offering?.[0]?.raw_text.slice(0, 120) ?? ''}
        items={(offering ?? []).map(toOfferItem)}
        onContinue={(chosen, note) => {
          const first = offering![0];
          setOfferDraft(curatedOfferDraft(first.raw_text.slice(0, 120), chosen, note));
          setOffering(null);
          setMessaging(first);
        }}
      />
    </>
  );
}
